import React, { useEffect, useState } from 'react';
import { Card, CardBody, CardHeader, Col, Container, Row, UncontrolledTooltip } from 'reactstrap';
import withRouter from '../.././components/withRouter';
import TableContainer from '../.././components/TableContainerReactTable';
import AddEditUserModal from './AddEditUserModal';
import { getUsers, impersonateUser } from '../../api/userApi';
import { telefoneMask } from '../../utils';
import { User, UserRole } from '../../types/user';
import { useProfile } from '../../hooks/UserHooks';

const Index = (props: any) => {
    const { userProfile } = useProfile();
    const [data, setData] = useState<User[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [dataToEdit, setDataToEdit] = useState<User | null>(null);
    const [isAddEditModalOpen, setIsAddModalOpen] = useState<boolean>(false);

    useEffect(() => {
        // Lista os publishers cadastrados na tabela
        getUsers()
            .then((res) => {
                setData(res);
            })
            .catch(() => {})
            .finally(() => {
                setLoading(false);
            });
    }, []);

    const triggerImpersonateUser = (id: number) => {
        impersonateUser(id)
        .then((res) => {
            if (res && res.access_token && res.refresh_token) {
                localStorage.setItem('access_token', res.access_token);
                localStorage.setItem('refresh_token', res.refresh_token);

                window.location.replace('/user');
            }
        })
        .catch(() => {})
        .finally(() => {})
    };

    const handleUpdateData = (newData: User) => {
        const updatedData = data.map((item) => {
            if (item.id === newData.id) {
                return newData;
            }
            return item;
        });
        setData(updatedData);
    };

    const handleAddData = (newData: User) => {
        setData([...data, newData]);
    };

    const toggleAddEditModal = () => {
        setIsAddModalOpen(!isAddEditModalOpen);
    };

    const columns = [
        {
            header: '#',
            cell: (cell: any) => {
                return (
                    <span className="fw-semibold">{cell.getValue()}</span>
                );
            },
            accessorKey: 'id',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Nome',
            accessorKey: 'name',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Empresa',
            accessorFn: (row: any) => {
                return row.publisher?.name ?? row.agency?.name ?? row.advertiser?.name ?? '-';
            },
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Email',
            accessorKey: 'email',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Telefone',
            accessorFn: (row: any) => {
                return row.phone ? telefoneMask(row.phone) : '-';
            },
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Cargo',
            accessorFn: (row: any) => {
                return row.jobTitle ?? '-';
            },
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Permissão',
            accessorFn: (row: any) => {
                return UserRole[row.role as keyof typeof UserRole] ?? '-';
            },
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Tipo',
            accessorFn: (row: any) => {
                return row.publisher ? 'Publisher' : (row.agency ? 'Agência' : (row.advertiser ? 'Anunciante' : 'Ad Pulse'));
            },
            cell: (cell: any) => {
                const row = cell.row.original;
                const badgeColor = row.publisher ? 'primary' : row.agency ? 'secondary' : row.advertiser ? 'success' : 'info';
                return <span className={`badge bg-${badgeColor}`}>{cell.getValue()}</span>;
            },
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Status',
            accessorKey: 'status',
            cell: (cell: any) => {
                const row = cell.row.original;
                if (row.status) {
                    return <span className={`badge bg-success-subtle text-success`}>ATIVO</span>;
                }

                return <span className={`badge bg-danger-subtle text-danger`}>DESATIVADO</span>;
            },
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            id: 'actions',
            cell: (cell: any) => {
                const row = cell.row.original;
                let actionCell = [];

                // Botão de editar
                actionCell.push(<div key="action_edit">
                    <button className="btn text-primary d-inline-block"
                            id={'action_edit_' + row.id}
                            onClick={() => {
                                setDataToEdit(row);
                                toggleAddEditModal();
                            }}
                    ><i className="ri-pencil-fill fs-16"></i></button>
                    <UncontrolledTooltip
                        placement="top"
                        target={'action_edit_' + row.id}
                    >
                        Editar
                    </UncontrolledTooltip>
                </div>);

                // Botão personificar
                if (userProfile && !userProfile.impersonatedUser && userProfile.role === 'ROLE_SUPER_ADMIN' && row.id !== userProfile.id) {
                    actionCell.push(<div key="action_impersonate">
                        <button className="btn text-primary d-inline-block"
                                id={'action_impersonate_' + row.id}
                                onClick={() => {
                                    triggerImpersonateUser(row.id);
                                }}
                        ><i className="ri-user-shared-fill fs-16"></i></button>
                        <UncontrolledTooltip
                            placement="top"
                            target={'action_impersonate_' + row.id}
                        >
                            Personificar
                        </UncontrolledTooltip>
                    </div>);
                }
                return <div className="d-flex gap-2">{actionCell}</div>;
            },
            enableColumnFilter: false,
            enableSorting: false,
        },
    ];

    document.title = 'Usuários | Ad Pulse';
    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardHeader>
                                    <Row className="g-4 align-items-center">
                                        <div className="col-sm">
                                            <h5 className="card-title mb-0">Usuários</h5>
                                        </div>
                                        <div className="col-sm-auto">
                                            <button
                                                type="button"
                                                className="btn btn-success add-btn me-1"
                                                id="create-btn"
                                                onClick={() => {
                                                    setDataToEdit(null);
                                                    toggleAddEditModal();
                                                }}
                                            >
                                                <i className="ri-add-line align-bottom me-1"></i> Adicionar usuário
                                            </button>
                                        </div>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                     {!loading ? <TableContainer
                                                divClass="table-responsive"
                                                columns={(columns || [])}
                                                data={(data || [])}
                                                isGlobalFilter={true}
                                                customPageSize={30}
                                                SearchPlaceholder="Procurar..."
                                            /> :
                                         <div className="text-center">
                                             <div className="spinner-border text-primary" role="status">
                                                 <span className="visually-hidden">Carregando...</span>
                                             </div>
                                         </div>}

                                    <AddEditUserModal
                                        user={dataToEdit}
                                        isOpen={isAddEditModalOpen}
                                        toggle={toggleAddEditModal}
                                        onAddData={handleAddData}
                                        onUpdateData={handleUpdateData}
                                    />
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default withRouter(Index);